import React, { useState, useEffect } from "react";

interface ExpenseCategory {
  id: number;
  name: string;
  description?: string;
}

interface GeneralTransactionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  editingTransaction?: any;
}

const GeneralTransactionForm: React.FC<GeneralTransactionFormProps> = ({
  isOpen,
  onClose,
  onSave,
  editingTransaction,
}) => {
  const [formData, setFormData] = useState({
    type: "expense",
    category_id: "",
    description: "",
    amount: "",
    payment_method: "cash",
    reference_number: "",
    vendor_name: "",
    receipt_number: "",
    transaction_date: new Date().toISOString().split("T")[0],
    notes: "",
    is_recurring: false,
    recurring_frequency: "",
  });

  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const colorTheme = {
    primary: "#667eea",
    success: "#10b981",
    error: "#ef4444",
    warning: "#f59e0b",
    gray50: "#f9fafb",
    gray100: "#f3f4f6",
    gray200: "#e5e7eb",
    gray500: "#6b7280",
    gray600: "#4b5563",
    gray900: "#1f2937",
    cardBg: "#ffffff",
  };

  const API_BASE =
    process.env.REACT_APP_API_URL ||
    "https://dr-bassam-elayoubi.caduceuslb.com/api";

  const paymentMethods = [
    { value: "cash", label: "Cash", icon: "💵" },
    { value: "card", label: "Credit/Debit Card", icon: "💳" },
    { value: "bank_transfer", label: "Bank Transfer", icon: "🏦" },
    { value: "check", label: "Check", icon: "📄" },
  ];

  const recurringFrequencies = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  // Fetch categories
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Initialize form when editing
  useEffect(() => {
    if (editingTransaction) {
      setFormData({
        type: editingTransaction.type,
        category_id: editingTransaction.category_id?.toString() || "",
        description: editingTransaction.description,
        amount: editingTransaction.amount.toString(),
        payment_method: editingTransaction.payment_method,
        reference_number: editingTransaction.reference_number || "",
        vendor_name: editingTransaction.vendor_name || "",
        receipt_number: editingTransaction.receipt_number || "",
        transaction_date: editingTransaction.transaction_date,
        notes: editingTransaction.notes || "",
        is_recurring: editingTransaction.is_recurring || false,
        recurring_frequency: editingTransaction.recurring_frequency || "",
      });
    } else {
      // Reset form
      setFormData({
        type: "expense",
        category_id: "",
        description: "",
        amount: "",
        payment_method: "cash",
        reference_number: "",
        vendor_name: "",
        receipt_number: "",
        transaction_date: new Date().toISOString().split("T")[0],
        notes: "",
        is_recurring: false,
        recurring_frequency: "",
      });
    }
    setErrors({});
  }, [editingTransaction, isOpen]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/transactions/categories/list`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const method = editingTransaction ? "PUT" : "POST";
      const url = editingTransaction
        ? `${API_BASE}/transactions/${editingTransaction.id}`
        : `${API_BASE}/transactions`;

      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        category_id: formData.category_id
          ? parseInt(formData.category_id)
          : null,
      };

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        onSave();
        onClose();
      } else {
        const errorData = await response.json();
        if (errorData.errors) {
          setErrors(errorData.errors);
        } else {
          setErrors({
            submit: errorData.error || "Failed to save transaction",
          });
        }
      }
    } catch (error) {
      console.error("Error saving transaction:", error);
      setErrors({ submit: "Network error. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
    >
      <div
        style={{
          background: colorTheme.cardBg,
          borderRadius: "16px",
          maxWidth: "700px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
        }}
      >
        {/* Header */}
        <div
          className="nn"
          style={{
            padding: "24px 32px",
            borderBottom: `1px solid ${colorTheme.gray200}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: colorTheme.gray900,
                margin: "0 0 4px 0",
              }}
            >
              {editingTransaction ? "Edit Transaction" : "Add New Transaction"}
            </h3>
            <p
              style={{
                color: colorTheme.gray500,
                fontSize: "14px",
                margin: 0,
              }}
            >
              Record income or expense not related to medical services
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              border: "none",
              background: colorTheme.gray100,
              color: colorTheme.gray500,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: "32px" }}>
          {/* Transaction Type & Category */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Transaction Type{" "}
                <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <div className="nn" style={{ display: "flex", gap: "12px" }}>
                {[
                  {
                    value: "income",
                    label: "Income",
                    color: colorTheme.success,
                  },
                  {
                    value: "expense",
                    label: "Expense",
                    color: colorTheme.error,
                  },
                ].map((type) => (
                  <label
                    htmlFor={`type_${type.value}`}
                    key={type.value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "12px 16px",
                      border: `2px solid ${
                        formData.type === type.value
                          ? type.color
                          : colorTheme.gray200
                      }`,
                      borderRadius: "8px",
                      cursor: "pointer",
                      background:
                        formData.type === type.value
                          ? `${type.color}15`
                          : "transparent",
                      color:
                        formData.type === type.value
                          ? type.color
                          : colorTheme.gray600,
                      fontWeight: formData.type === type.value ? "600" : "400",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <input
                      type="radio"
                      id={`type_${type.value}`} // Added missing id
                      name="type"
                      value={type.value}
                      checked={formData.type === type.value}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }));
                      }}
                      style={{
                        width: "16px",
                        height: "16px",
                        marginRight: "4px", // Make radio visible and styled
                      }}
                    />
                    {type.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label
                htmlFor="Category"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Category
              </label>
              <select
                value={formData.category_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    category_id: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colorTheme.cardBg,
                }}
              >
                <option value="">Select category (optional)</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Description & Amount */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            <div>
              <label
                htmlFor="Description"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Description <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.description ? colorTheme.error : colorTheme.gray200
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
                placeholder="Enter transaction description..."
                required
              />

              {errors.description && (
                <p
                  style={{
                    color: colorTheme.error,
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.description}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="Amount"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Amount <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, amount: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.amount ? colorTheme.error : colorTheme.gray200
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
                placeholder="0.00"
                min="0"
                step="0.01"
                required
              />

              {errors.amount && (
                <p
                  style={{
                    color: colorTheme.error,
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.amount}
                </p>
              )}
            </div>
          </div>

          {/* Payment Method & Date */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            <div>
              <label
                htmlFor="payment_method"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Payment Method{" "}
                <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <select
                id="payment_method"
                value={formData.payment_method}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    payment_method: e.target.value as any,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colorTheme.cardBg,
                }}
                required
                autoComplete="cc-type"
              >
                {paymentMethods.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.icon} {method.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="transaction_date"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Transaction Date{" "}
                <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <input
                type="date"
                id="transaction_date"
                name="transaction_date"
                value={formData.transaction_date}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    transaction_date: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
                required
                autoComplete="off"
              />
            </div>
          </div>

          {/* Vendor & Reference Details */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div>
              <label
                htmlFor="vendor_name"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Vendor/Payee
              </label>
              <input
                type="text"
                id="vendor_name"
                name="vendor_name"
                value={formData.vendor_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    vendor_name: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
                placeholder="Vendor name..."
                autoComplete="organization"
              />
            </div>

            <div>
              <label
                htmlFor="reference_number"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Reference Number
              </label>
              <input
                type="text"
                id="reference_number"
                name="reference_number"
                value={formData.reference_number}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    reference_number: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
                placeholder="Ref. number..."
              />
            </div>

            <div>
              <label
                htmlFor="receipt_number"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Receipt Number
              </label>
              <input
                type="text"
                id="receipt_number"
                name="receipt_number"
                value={formData.receipt_number}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    receipt_number: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
                placeholder="Receipt #..."
              />
            </div>
          </div>

          {/* Recurring Transaction */}
          <div
            style={{
              background: colorTheme.gray50,
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <label
              htmlFor="is_recurring"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                fontSize: "14px",
                fontWeight: "500",
                color: colorTheme.gray900,
                marginBottom: "16px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                id="is_recurring"
                name="is_recurring"
                checked={formData.is_recurring}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    is_recurring: e.target.checked,
                  }))
                }
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                }}
              />
              🔄 Recurring Transaction
            </label>

            {formData.is_recurring && (
              <div>
                <label
                  htmlFor="recurring_frequency"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colorTheme.gray600,
                    marginBottom: "8px",
                  }}
                >
                  Recurring Frequency
                </label>
                <select
                  value={formData.recurring_frequency}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      recurring_frequency: e.target.value as any,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `2px solid ${colorTheme.gray200}`,
                    borderRadius: "8px",
                    fontSize: "14px",
                    background: colorTheme.cardBg,
                  }}
                  required={formData.is_recurring}
                >
                  <option value="">Select frequency...</option>
                  {recurringFrequencies.map((freq) => (
                    <option key={freq.value} value={freq.value}>
                      {freq.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Notes */}
          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="notes"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: colorTheme.gray600,
                marginBottom: "8px",
              }}
            >
              Notes
            </label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              rows={3}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${colorTheme.gray200}`,
                borderRadius: "8px",
                fontSize: "14px",
                resize: "none",
              }}
              placeholder="Additional notes or comments..."
            />
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div
              style={{
                padding: "12px 16px",
                background: "#fecaca",
                border: "1px solid #fca5a5",
                borderRadius: "8px",
                color: "#991b1b",
                fontSize: "14px",
                marginBottom: "24px",
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Form Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              paddingTop: "20px",
              borderTop: `1px solid ${colorTheme.gray200}`,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "12px 24px",
                border: `1px solid ${colorTheme.gray200}`,
                borderRadius: "8px",
                background: colorTheme.cardBg,
                color: colorTheme.gray600,
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                background: loading
                  ? colorTheme.gray200
                  : formData.type === "income"
                  ? colorTheme.success
                  : colorTheme.error,
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading
                ? "Saving..."
                : editingTransaction
                ? "Update Transaction"
                : `Record ${formData.type === "income" ? "Income" : "Expense"}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GeneralTransactionForm;
